import { isAxiosError } from 'axios'

import { useAuthStore } from '@/auth/useAuthStore'
import { type CoreLocation } from '@/core/store/types'
import { logInfo } from '@/logger'
import { OlympusClient } from '@/OlympusClient'

import { useCoreStore } from '../useCoreStore'

/**
 * Fetches "core" data and persists to pinia store
 * @param brandId id of the brand to fetch
 */
export const loadBrand = async (brandId: string): Promise<void> => {
  const coreStore = useCoreStore()
  const authStore = useAuthStore()

  let response
  try {
    response = await OlympusClient.dashboard.v3.brands.brand.core({
      headers: { 'X-Active-Brand-ID': brandId },
      params: { brandId },
    })
  } catch (error) {
    let statusCode: number | null = null
    let errorCode: string | null = null
    let requestId: string | null = null
    if (OlympusClient.dashboard.v3.brands.brand.core.isError(error)) {
      statusCode = error.status
      errorCode = error.code
    } else if (isAxiosError(error)) {
      statusCode = error.response?.status ?? null
      const code = error.response?.data?.code
      errorCode = typeof code === 'string' ? code : null
      const header = error.response?.headers?.['x-request-id']
      requestId = typeof header === 'string' ? header : null
    }
    logInfo('brand_core.load_failed', {
      attemptedBrandId: brandId,
      errorCode,
      persistedBrandId: authStore.brandSession?.brandId ?? null,
      requestId,
      statusCode,
    })
    throw error
  }

  const { brand, locations, theme, session, allBrandLocations } = response

  if (brand.deletedAt) {
    logInfo('brand_core.deleted_brand_loaded', {
      attemptedBrandId: brandId,
      persistedBrandId: authStore.brandSession?.brandId ?? null,
    })
  }

  coreStore.setActiveBrand({
    ...brand,
    locations: locations as CoreLocation[],
    theme,
  })

  coreStore.setAllBrandLocations(allBrandLocations)

  if (session) {
    authStore.setBrandSession(session)
    await authStore.reinitializeAuthorizer('loadBrand')
  }
}
